import type { Job, StockItem, StockRegion, User, SecretaryNotification } from "../types";

const MAX_RECENT_COMPLETED_JOBS = 5;

export interface SecretaryNotificationsParams {
  jobs: Job[];
  stock: StockItem[];
  installers: User[];
  partners: User[];
  stockRegions: StockRegion[];
}

/**
 * Calcule la liste des notifications pour la mini secrétaire admin
 * (dossiers en attente, poseurs ayant terminé, stock bas).
 */
export function computeSecretaryNotifications(params: SecretaryNotificationsParams): SecretaryNotification[] {
  const { jobs, stock, installers, partners, stockRegions } = params;
  const notifications: SecretaryNotification[] = [];

  // Dossiers en attente
  const pendingJobs = jobs.filter((j) => j.status === "pending");
  if (pendingJobs.length > 0) {
    notifications.push({
      id: "pending_jobs",
      type: "pending_jobs",
      shortMessage:
        pendingJobs.length === 1
          ? "Vous avez 1 dossier en attente"
          : `Vous avez ${pendingJobs.length} dossiers en attente`,
      fullMessage: `Vous avez ${pendingJobs.length} dossier${pendingJobs.length > 1 ? "s" : ""} en attente. Consultez l'onglet Dossiers pour les planifier.`,
      action: { tab: "jobs", label: "Voir les dossiers" },
    });
  }

  // Poseurs ayant terminé (N derniers chantiers)
  const completedJobs = jobs
    .filter((j) => j.status === "completed")
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, MAX_RECENT_COMPLETED_JOBS);

  if (completedJobs.length > 0) {
    const lines: string[] = [];
    const getName = (id: string) => installers.find((i) => i.id === id)?.name ?? "Poseur";
    const getStockLabel = (itemId: string) => stock.find((s) => s.id === itemId)?.name ?? itemId;

    for (const job of completedJobs) {
      const installerName = job.installerId ? getName(job.installerId) : "—";
      const parts: string[] = [`${installerName} a terminé le chantier ${job.clientName}`];
      const neuf = job.stockUsedNeuf && Object.keys(job.stockUsedNeuf).length > 0;
      const remplacement = job.stockUsedRemplacement && Object.keys(job.stockUsedRemplacement).length > 0;
      if (neuf || remplacement) {
        const used: string[] = [];
        if (job.stockUsedNeuf) {
          for (const [id, qty] of Object.entries(job.stockUsedNeuf)) {
            if (qty > 0) used.push(`${getStockLabel(id)} (neuf) : ${qty}`);
          }
        }
        if (job.stockUsedRemplacement) {
          for (const [id, qty] of Object.entries(job.stockUsedRemplacement)) {
            if (qty > 0) used.push(`${getStockLabel(id)} (remplacement) : ${qty}`);
          }
        }
        if (used.length > 0) parts.push(`Stock utilisé : ${used.join(", ")}`);
      }
      if (job.installerNotes?.trim()) {
        parts.push(`Commentaire : ${job.installerNotes.trim()}`);
      }
      lines.push(parts.join(". "));
    }

    const shortMessage =
      completedJobs.length === 1
        ? "Un poseur a terminé son installation"
        : `${completedJobs.length} chantiers viennent d'être terminés`;
    const fullMessage = lines.join("\n\n");

    notifications.push({
      id: "completed_jobs",
      type: "completed_jobs",
      shortMessage,
      fullMessage,
      action: { tab: "jobs", label: "Voir les dossiers" },
    });
  }

  // Stock bas : grouper par région puis par partenaire
  const lowStockItems = stock.filter((item) => item.quantity <= (item.lowStockThreshold ?? 10));
  if (lowStockItems.length > 0) {
    const byRegion = new Map<string, StockItem[]>();
    const byPartner = new Map<string | null, StockItem[]>();

    for (const item of lowStockItems) {
      const regionId = item.regionId ?? "_none_";
      if (!byRegion.has(regionId)) byRegion.set(regionId, []);
      byRegion.get(regionId)!.push(item);

      const partnerId = item.partnerId ?? null;
      if (!byPartner.has(partnerId)) byPartner.set(partnerId, []);
      byPartner.get(partnerId)!.push(item);
    }

    // Une notif par région concernée (région avec au moins un article en stock bas)
    const regionIds = Array.from(byRegion.keys()).filter((id) => id !== "_none_");
    for (const regionId of regionIds) {
      const items = byRegion.get(regionId)!;
      const region = stockRegions.find((r) => r.id === regionId);
      const regionName = region?.name ?? "cette région";
      const count = items.length;
      notifications.push({
        id: `low_stock_region_${regionId}`,
        type: "low_stock",
        shortMessage: `Stock bas dans la région ${regionName}`,
        fullMessage: `Il vous reste peu de stock dans la région ${regionName} (${count} article${count > 1 ? "s" : ""} sous le seuil). Consultez l'onglet Stock.`,
        action: { tab: "stock", label: `Voir le stock – ${regionName}`, regionId },
      });
    }

    // Stock interne (sans région ou partenaire)
    const internal = lowStockItems.filter((i) => !i.regionId && i.partnerId == null);
    if (internal.length > 0) {
      notifications.push({
        id: "low_stock_internal",
        type: "low_stock",
        shortMessage: "Stock interne sous le seuil",
        fullMessage: `${internal.length} article${internal.length > 1 ? "s" : ""} en stock interne sous le seuil. Consultez l'onglet Stock.`,
        action: { tab: "stock", label: "Voir le stock" },
      });
    }
  }

  return notifications;
}
